import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {useLocation} from 'react-router-dom';

import {
  isWorkspaceLoaderPath,
  WorkspaceLoader,
} from '../pages/ProjectPage/WorkspaceLoader';
import {InstrumentedFloatingWaveLoader} from './../components/utility/InstrumentedLoader';
import {envIsDev} from './../config';

type AppLoaderContextType = {
  addLoader: (name: string) => void;
  removeLoader: (name: string) => void;
};
const AppLoaderContext = React.createContext<AppLoaderContextType>({
  addLoader: () => {},
  removeLoader: () => {},
});

export const AppLoaderContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [loaders, setLoaders] = useState<string[]>([]);

  const addLoader = useCallback((name: string) => {
    setLoaders(prev => [...prev, name]);
  }, []);

  const removeLoader = useCallback((name: string) => {
    setLoaders(prev => prev.filter(n => n !== name));
  }, []);

  const value = useMemo(
    () => ({
      addLoader,
      removeLoader,
    }),
    [addLoader, removeLoader]
  );

  const {pathname} = useLocation();
  const shouldUseWorkspaceLoader = isWorkspaceLoaderPath(pathname);

  return (
    <AppLoaderContext.Provider value={value}>
      {loaders.length > 0 && !shouldUseWorkspaceLoader && (
        <InstrumentedFloatingWaveLoader name="floating-loader" size="huge" />
      )}
      {loaders.length > 0 && shouldUseWorkspaceLoader && <WorkspaceLoader />}
      {children}
    </AppLoaderContext.Provider>
  );
};

const debugLoaderProxy = envIsDev && false;

/**
 * ⚠️ Use with caution — LoaderProxy impacts global app state and can block interaction with
 * large portions of the UI if overused. Reserve it for truly blocking operations.
 *
 * LoaderProxy:
 * For components that trigger app- or route-level loading (e.g. project/workspace fetches).
 * It hooks chained loading events into a single loader with AppLoaderContext so that the app
 * maintains a single, persistent global spinner until all active loaders complete.
 *
 * This lets multiple nested components signal loading independently without restarting
 * or flickering the spinner animation as they mount and unmount.
 *
 * ✅ Use for: page-level loads, major data fetches, or transitions that block the app —
 *     especially when loading can cascade across components.
 * ❌ Avoid for: small local loaders (tables, buttons, cards) — those should manage their own
 *     state. Using LoaderProxy there would show the full-screen spinner for minor actions
 *     and cause unnecessary global re-renders.
 *
 * Multiple LoaderProxies can be active simultaneously; only one spinner is ever shown,
 * and it stays visible until the last loader finishes.
 */
export const LoaderProxy = ({name}: {name: string}) => {
  const {addLoader, removeLoader} = useContext(AppLoaderContext);
  useEffect(() => {
    const mount = performance.now();
    addLoader(name);
    return () => {
      const duration = performance.now() - mount;
      if (debugLoaderProxy) {
        console.log(`${name} unmounted in ${duration}ms`);
      }
      removeLoader(name);
    };
  }, [addLoader, removeLoader, name]);
  return null;
};
